import { UserRole } from '@prisma/client'

import {
  DECIDING_POST,
  postRole,
  PROJECT_POST_KEYS,
  type ProjectPost,
} from './project-posts'
import {
  ADMIN_ROLES,
  ADVISOR_ROLES,
  holdsAny,
  PROJECT_SCOPED_ROLES,
} from './user-roles'

/**
 * Who changes a project, and why — every rule about it, as pure functions of
 * roles and what the project says about a person.
 *
 * `ProjectAccessGuard`, `ProjectsService`, `ProjectCollaboratorsService` and
 * the screens only gather those facts — a uuid compared on the server, an id
 * in the browser — and ask here, so none of them can come to answer
 * differently.
 */

/**
 * Why somebody may change a project: they administer every project, they hold
 * one of its posts, or they were given access beside the people it names.
 */
export type ProjectAccess = 'administration' | ProjectPost | 'collaborator'

/** What a project says about one person. */
export type ProjectRelation = ProjectPost | 'collaborator' | null

/** Whether an account administers every project. */
export const administersProjects = (
  roles: readonly UserRole[] | null | undefined,
): boolean => holdsAny(roles, ADMIN_ROLES)

/** Whether an account is held to the projects it is named on or was given. */
export const isProjectScoped = (
  roles: readonly UserRole[] | null | undefined,
): boolean => holdsAny(roles, PROJECT_SCOPED_ROLES)

/** Whether an account may be given access to a project: the accounts held to
 *  their projects are the ones projects are handed to. */
export const mayCollaborate = isProjectScoped

/**
 * Whether an account may be named for a post.
 *
 * The post's own role, or the administration — which changes every project
 * anyway, and is who a small team names when nobody else holds the role yet.
 */
export const mayHoldPost = (
  roles: readonly UserRole[] | null | undefined,
  post: ProjectPost,
): boolean => administersProjects(roles) || !!roles?.includes(postRole(post))

/**
 * What a project says about a person, from the facts it holds.
 *
 * A post names them ahead of having been given access, and the posts are
 * weighed in their own order — somebody who is both the advisor and the site
 * manager is named as the advisor, which is the post that decides.
 */
export const relationOf = (facts: {
  posts: readonly ProjectPost[]
  collaborates: boolean
}): ProjectRelation =>
  PROJECT_POST_KEYS.find(post => facts.posts.includes(post)) ??
  (facts.collaborates ? 'collaborator' : null)

/**
 * The access an account has to a project, or null where it may only read it.
 *
 * An administrator changes every project. An account held to its projects
 * changes the ones it is named on or was given; every other role changes none.
 */
export function projectAccessOf(
  roles: readonly UserRole[] | null | undefined,
  relation: ProjectRelation,
): ProjectAccess | null {
  if (administersProjects(roles)) return 'administration'
  if (!isProjectScoped(roles)) return null
  return relation
}

/**
 * Whether an account may write what hangs off a project without being it: its
 * notes, and the documents filed on it.
 *
 * The rule `ProjectAccessGuard` applies to a `@ProjectScope` route — a role
 * held to its own projects needs one of them, every other internal role
 * reaches all of them. What the project *is* stays with `projectAccessOf`.
 */
export const worksOnProject = (
  roles: readonly UserRole[] | null | undefined,
  access: ProjectAccess | null,
): boolean => !isProjectScoped(roles) || access !== null

/**
 * Whether an account may start a project.
 *
 * The advisor's: a project begins with somebody answering for it, and the
 * roles that run one work on the projects that exist.
 */
export const mayCreateProject = (
  roles: readonly UserRole[] | null | undefined,
): boolean => holdsAny(roles, ADVISOR_ROLES)

/**
 * Whether an access decides about the project itself — removing it, who holds
 * its posts, and who else works on it. A project manager changes everything
 * the project is made of and none of this: free to staff it, they could hand
 * it to anyone.
 */
export const decidesOnProject = (access: ProjectAccess | null): boolean =>
  access === 'administration' || access === DECIDING_POST
